import * as React from 'react';

import { Dropdown, Loader } from 'semantic-ui-react';
import merge from 'lodash/merge';

import { ErrorBoundary } from '../../containers';
import { localeData } from '../../locales/data';
import { addModifiers, getLanguageCode } from '../../shared/helpers';
import { isMobile } from '../../shared/helpers/responsive';

import { ChartProps, GetChartProps } from './types';
import './Chart.scss';
import ChartButtons from './ChartButtons';
import {
  chartStyleVerdanafontWeight500Small,
  chartStyleVerdanaNormal,
  getHeightChart,
  highstockChart,
  highstockConfigxAxis,
  highstockConfigyAxisSingle,
  highstockConfigChart,
  highstockConfigNavigator,
  highstockConfigTooltip,
} from './ChartSettings';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { DeepPartial } from 'utility-types';
import Highcharts from 'highcharts';
import { TestPeriodControl } from '../Alphas/AlphasDetails/TestPeriodControl';

const HighchartsReact = React.lazy(() => import('../../shared/helpers/highcharts'));
const HighchartsHighStockReact = React.lazy(() => import('../../shared/helpers/highcharts-highstock'));

interface ChartState {
  windowIsResizing: number;
  extremes: any;
}

const twoDecimalCaseOption = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

export const getChartNavigatorScrollbarSets = (position?: string) => {
  const color = position === 'even' ? 'white' : '#f7f9fb';

  return {
    enabled: true,
    zIndex: 33333333,
    barBackgroundColor: color,
    barBorderRadius: 7,
    barBorderWidth: 0,
    buttonBackgroundColor: color,
    buttonArrowColor: color,
    buttonBorderWidth: 0,
    buttonBorderRadius: 7,
    trackBackgroundColor: color,
    trackBorderColor: color,
    trackBorderWidth: 1,
    trackBorderRadius: 8,
  };
};

interface ConfigPerformanceChartsParams {
  chartDataSchema: any;
  chartDataRecords: any;
  chartName?: string;
  _: any;
  recordSet: string;
  handleSetExtremes: (extremes: any) => void;
  fatherClass?: string;
  position?: string;
  updateSelectedSeries?: (e: any) => void;
  hasLegend?: boolean;
  yAxisConfig?: Record<string, any>[];
  mergeConfig?: DeepPartial<{
    chart: { className: string };
    legend: { useHTML: boolean; labelFormatter: (this: { name: string; color: string }) => string };
  }>;
  customNavigatorSize?: number;
}

// tslint:disable-next-line:max-func-body-length
export const configPerformanceCharts = ({
  chartDataSchema,
  chartDataRecords,
  chartName,
  recordSet,
  handleSetExtremes,
  fatherClass,
  position,
  updateSelectedSeries,
  hasLegend = false,
  yAxisConfig,
  mergeConfig = {},
  customNavigatorSize,
}: ConfigPerformanceChartsParams) => {
  const isArea = recordSet === 'dataset-coverage' || recordSet === 'valueRange';

  return merge(
    {
      credits: { enabled: false },
      ...highstockConfigNavigator,
      rangeSelector: {
        enabled: false,
      },
      ...highstockConfigxAxis,
      scrollbar: getChartNavigatorScrollbarSets(position),
      xAxis: {
        events: {
          setExtremes: (e: any) => {
            setTimeout(() => handleSetExtremes({ userMax: e.userMax, userMin: e.userMin }), 100);
          },
        },
      },
      yAxis: yAxisConfig ? yAxisConfig : highstockConfigyAxisSingle(chartDataSchema.properties[1].type).yAxis,
      ...highstockConfigChart({
        fatherClass: fatherClass,
        isArea: isArea,
        customNavigatorSize,
      }),
      ...highstockConfigTooltip({ type: chartDataSchema.properties[1].type, recordSet: recordSet, isDateNotConverted: true }),
      plotOptions: {
        series: {
          events: {
            legendItemClick: updateSelectedSeries,
          },
        },
      },
      legend: {
        enabled: hasLegend,
        itemDistance: 40,
        itemMarginTop: 2,
        itemMarginBottom: 2,
        itemStyle: {
          ...chartStyleVerdanafontWeight500Small,
        },
      },
      series: chartDataRecords,
      title: {
        text: chartName === 'Distribution' ? chartDataSchema.title : null,
        style: chartStyleVerdanafontWeight500Small,
      },
    },
    mergeConfig
  );
};

interface ConfigPerformanceProfileParams {
  chartDataSchema: any;
  chartDataRecords: any;
  type: string;
  handleSetExtremes: (extremes: any) => void;
  valueType?: string;
  currency?: string | null;
  isStacked?: boolean;
  isTooltipTotal?: boolean;
  position?: string;
}

export const configPerformanceProfile = ({
  chartDataSchema,
  chartDataRecords,
  type,
  handleSetExtremes,
  valueType = 'decimal',
  currency,
  isStacked = false,
  isTooltipTotal = false,
  position,
}: ConfigPerformanceProfileParams) => ({
  credits: { enabled: false },
  ...highstockConfigNavigator,
  rangeSelector: {
    enabled: false,
  },
  scrollbar: getChartNavigatorScrollbarSets(position),
  xAxis: {
    type: 'datetime',
    tickInterval: 24 * 3600 * 1000,
    events: {
      setExtremes: (e: any) => {
        setTimeout(() => handleSetExtremes({ userMax: e.userMax, userMin: e.userMin }), 100);
      },
    },
  },
  ...highstockConfigyAxisSingle(chartDataSchema.properties[1].type),
  chart: {
    ...highstockConfigChart({}).chart,
    type,
    height: isMobile('mobile') ? 200 : 350,
  },
  ...highstockConfigTooltip({
    type: chartDataSchema.properties[1].type,
    recordSet: 'coverage',
    valueType: valueType,
    currency: currency,
    isTotal: isTooltipTotal,
    isDateNotConverted: true,
  }),
  series: chartDataRecords,
  ...(isStacked && {
    plotOptions: {
      column: {
        stacking: 'normal',
      },
    },
  }),
});

const configPieChart = (chartDataSchema: any, chartDataRecords: any, updateSelectedSeries: (e: any) => void, fatherClass?: string) => ({
  credits: { enabled: false },
  chart: {
    height: getHeightChart({ elementClass: fatherClass }),
    type: 'pie',
    style: chartStyleVerdanaNormal,
  },
  tooltip: {
    pointFormatter: function (this: any): any {
      return `<b>${(this.y * 100).toLocaleString(getLanguageCode())}%</b>`;
    },
  },
  title: {
    text: null,
    style: chartStyleVerdanafontWeight500Small,
  },
  xAxis: {
    categories: chartDataSchema.properties.map((property: any) => property.title),
  },
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: 'pointer',
      dataLabels: {
        enabled: true,
        format: '<b>{point.name}</b>',
      },
      showInLegend: true,
    },
    series: {
      point: {
        events: {
          legendItemClick: updateSelectedSeries,
        },
      },
    },
  },
  series: chartDataRecords,
});

interface ConfigHighStockChartParams {
  chartDataSchema: any;
  chartDataRecords: any;
  updateSelectedSeries: (e: any) => void;
  position: string | undefined;
  fatherClass?: string;
  hasLegend?: boolean;
  customNavigatorSize?: number;
}

const configHighStockChart = ({
  chartDataSchema,
  chartDataRecords,
  updateSelectedSeries,
  position,
  fatherClass,
  hasLegend = true,
  customNavigatorSize,
}: ConfigHighStockChartParams) => ({
  credits: { enabled: false },
  ...highstockConfigNavigator,
  rangeSelector: {
    enabled: false,
  },
  ...highstockConfigxAxis,
  scrollbar: getChartNavigatorScrollbarSets(position),
  ...highstockConfigyAxisSingle(chartDataSchema.properties[1].type),
  ...highstockConfigChart({ fatherClass: fatherClass, customNavigatorSize }),
  ...highstockChart(chartDataSchema.properties[1].type),
  plotOptions: {
    series: {
      events: {
        legendItemClick: updateSelectedSeries,
      },
    },
  },
  legend: {
    enabled: hasLegend,
    itemDistance: 40,
    itemMarginTop: 2,
    itemMarginBottom: 2,
    itemStyle: {
      ...chartStyleVerdanafontWeight500Small,
    },
  },
  series: chartDataRecords,
});

const configBarChart = (
  chartDataSchema: any,
  chartDataRecords: any,
  chartName: string,
  updateSelectedSeries: (e: any) => void,
  fatherClass?: string
) => {
  const schemaData =
    chartName === 'coverage' || chartName === 'dataset-coverage'
      ? chartDataSchema.properties.filter((property: any) => property.name !== 'series')
      : chartDataSchema.properties;

  return {
    credits: { enabled: false },
    chart: {
      height: getHeightChart({ elementClass: fatherClass }),
      type: 'column',
      style: chartStyleVerdanaNormal,
    },
    title: {
      text: chartName === 'coverage' || chartName === 'dataset-coverage' || 'Sharpe' ? null : chartDataSchema.title,
      style: chartStyleVerdanafontWeight500Small,
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick: updateSelectedSeries,
        },
      },
    },
    yAxis: {
      showEmpty: false,
      title: {
        text: chartName === 'coverage' || chartName === 'dataset-coverage' ? '% Covered' : chartName,
        style: chartStyleVerdanafontWeight500Small,
      },
      max: chartName === 'coverage' || chartName === 'dataset-coverage' ? 100 : null,
      labels: {
        formatter: function (this: any) {
          const value = this.value;
          const numericSymbols = ['', 'K', 'M', 'B', 'T'];
          const breaks = [5, 8, 11, 14, 17];
          const numberSize = Math.ceil(Math.log(Math.abs(value) + 1) / Math.LN10);
          const index = breaks.findIndex((element: number) => numberSize < element);
          let ret;
          if (index === 0) {
            ret = `${value.toLocaleString(getLanguageCode())}`;
          } else {
            ret = `${(value / Math.pow(10, breaks[index - 1] - 2)).toLocaleString(getLanguageCode())}${numericSymbols[index]}`;
          }

          return ret;
        },
      },
    },
    xAxis: {
      categories: schemaData.map((property: any) => property.title),
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: getBarChartTooltipFormatter(chartName),
    },
    series: chartDataRecords,
  };
};

const distributionYAxisConfig = [
  {
    labels: {
      format: '{value}',
    },
    title: {
      text: 'Fraction of all values',
    },
    opposite: false,
  },
  {
    title: {
      text: 'CDF',
    },
    labels: {
      format: '{value}',
    },
    opposite: true,
  },
];

interface ConfigConsultantParameters {
  chartDataSchema: any;
  chartDataRecords: any;
  recordSetsOption: string;
  alphaInfo: any;
  updateSelectedSeries: (e: any) => void;
  handleSetExtremes: (extremes: any) => void;
  fatherClass?: string;
  valueType?: string;
  currency?: string | null;
  position?: string;
  customNavigatorSize?: number;
}

// tslint:disable-next-line:cyclomatic-complexity
const getConfigConsultant = ({
  chartDataSchema,
  chartDataRecords,
  recordSetsOption,
  alphaInfo,
  updateSelectedSeries,
  handleSetExtremes,
  fatherClass,
  valueType,
  currency,
  position,
  customNavigatorSize,
}: ConfigConsultantParameters) => {
  switch (recordSetsOption) {
    case 'average-size-by-capitalization':
    case 'average-size-by-country':
    case 'average-size-by-industry':
    case 'average-size-by-sector':
    case 'average-size-by-geography':
      return configPieChart(chartDataSchema, chartDataRecords, updateSelectedSeries, fatherClass);
    case 'actual-weight':
      return configHighStockChart({
        chartDataSchema: chartDataSchema,
        chartDataRecords: chartDataRecords,
        updateSelectedSeries: updateSelectedSeries,
        position: position,
        fatherClass: fatherClass,
        hasLegend: false,
        customNavigatorSize,
      });
    case 'average-value-by-country':
    case 'average-value-by-industry':
    case 'average-value-by-sector':
    case 'average-value-by-geography':
      return configHighStockChart({
        chartDataSchema: chartDataSchema,
        chartDataRecords: chartDataRecords,
        updateSelectedSeries: updateSelectedSeries,
        position: position,
        fatherClass: fatherClass,
        customNavigatorSize,
      });
    case 'coverage-by-country':
    case 'coverage-by-industry':
    case 'coverage-by-sector':
    case 'coverage-by-geography':
      return configBarChart(chartDataSchema, chartDataRecords, 'coverage', updateSelectedSeries, fatherClass);
    case 'pnl-by-capitalization':
    case 'pnl-by-country':
    case 'pnl-by-industry':
    case 'pnl-by-sector':
    case 'pnl-by-geography':
      return configHighStockChart({
        chartDataSchema: chartDataSchema,
        chartDataRecords: chartDataRecords,
        updateSelectedSeries: updateSelectedSeries,
        position: position,
        fatherClass: fatherClass,
        customNavigatorSize,
      });
    case 'sharpe-by-industry':
    case 'sharpe-by-country':
    case 'sharpe-by-sector':
    case 'sharpe-by-capitalization':
    case 'sharpe-by-geography':
      return configBarChart(chartDataSchema, chartDataRecords, 'Sharpe', updateSelectedSeries, fatherClass);
    case 'pnl':
    case 'coverage':
    case 'turnover':
    case 'sharpe':
    case 'dataset-coverage':
      return configPerformanceCharts({
        chartDataSchema: chartDataSchema,
        chartDataRecords: chartDataRecords,
        _: alphaInfo,
        recordSet: recordSetsOption,
        handleSetExtremes: handleSetExtremes,
        fatherClass: fatherClass,
        position: position,
        updateSelectedSeries: updateSelectedSeries,
        hasLegend: chartDataRecords.length > 1,
        customNavigatorSize,
      });
    case 'valueRange':
      return configPerformanceCharts({
        chartDataSchema: chartDataSchema,
        chartDataRecords: chartDataRecords,
        _: alphaInfo,
        recordSet: recordSetsOption,
        handleSetExtremes: handleSetExtremes,
        fatherClass: fatherClass,
        position: position,
        updateSelectedSeries: updateSelectedSeries,
        hasLegend: true,
        mergeConfig: dataExplorerConfig,
      });
    case 'distribution':
      return configPerformanceCharts({
        chartDataSchema: chartDataSchema,
        chartDataRecords: chartDataRecords,
        chartName: 'Distribution',
        _: alphaInfo,
        recordSet: recordSetsOption,
        handleSetExtremes: handleSetExtremes,
        fatherClass: fatherClass,
        position: position,
        updateSelectedSeries: updateSelectedSeries,
        hasLegend: true,
        yAxisConfig: distributionYAxisConfig,
        mergeConfig: dataExplorerConfig,
      });
    case 'performance-area':
      return configPerformanceProfile({
        chartDataSchema,
        chartDataRecords,
        type: 'area',
        handleSetExtremes,
        valueType,
        currency,
        position,
      });
    case 'performance-columns':
      return configPerformanceProfile({
        chartDataSchema,
        chartDataRecords,
        type: 'column',
        handleSetExtremes,
        valueType,
        currency,
        position,
      });
    case 'performance-stacked-columns':
      return configPerformanceProfile({
        chartDataSchema,
        chartDataRecords,
        type: 'column',
        handleSetExtremes,
        valueType,
        currency,
        isStacked: true,
        isTooltipTotal: true,
        position,
      });
    default:
      return {};
  }
};

const highChartsNames = [
  'average-size-by-country',
  'average-size-by-capitalization',
  'average-size-by-industry',
  'average-size-by-sector',
  'average-size-by-geography',
  'coverage-by-country',
  'coverage-by-industry',
  'coverage-by-sector',
  'coverage-by-geography',
  'sharpe-by-industry',
  'sharpe-by-country',
  'sharpe-by-sector',
  'sharpe-by-capitalization',
  'sharpe-by-geography',
  'distribution',
];

const GetChart = ({
  chartDataSchema,
  chartDataRecords,
  recordSetsOption,
  handleChartRef,
  alphaInfo,
  updateSelectedSeries,
  fatherClass,
  handleSetExtremes,
  valueType = 'decimal',
  currency = null,
  position,
  customNavigatorSize,
}: GetChartProps) => (
  <ErrorBoundary isNotFullScreen={true}>
    <React.Suspense
      fallback={
        <div className="loader">
          <Loader active={true} inline="centered" size="massive" />
        </div>
      }
    >
      {highChartsNames.indexOf(recordSetsOption) > -1 ? (
        <HighchartsReact
          callback={handleChartRef}
          options={getConfigConsultant({
            chartDataSchema: chartDataSchema,
            chartDataRecords: chartDataRecords,
            recordSetsOption: recordSetsOption,
            alphaInfo: alphaInfo,
            updateSelectedSeries: updateSelectedSeries,
            handleSetExtremes: handleSetExtremes,
            fatherClass: fatherClass,
            valueType: valueType,
            currency: currency,
            position: position,
            customNavigatorSize,
          })}
        />
      ) : (
        <HighchartsHighStockReact
          callback={handleChartRef}
          constructorType="stockChart"
          options={getConfigConsultant({
            chartDataSchema: chartDataSchema,
            chartDataRecords: chartDataRecords,
            recordSetsOption: recordSetsOption,
            alphaInfo: alphaInfo,
            updateSelectedSeries: updateSelectedSeries,
            handleSetExtremes: handleSetExtremes,
            fatherClass: fatherClass,
            valueType: valueType,
            currency: currency,
            position: position,
            customNavigatorSize,
          })}
        />
      )}
    </React.Suspense>
  </ErrorBoundary>
);

const performanceArray = [{ text: 'PnL', value: 'pnl', key: 'pnl' }];

const handleChangeRecordSetsTest = () => true;

export const getOffset = (element: any) => {
  const bound = element.getBoundingClientRect();
  const html: any = document.documentElement ? document.documentElement : { clientTop: 0 };
  const boundTop: number = bound.top;
  const offsset: number = window.pageYOffset;
  const htmlTop: number = html.clientTop;

  return boundTop + offsset - htmlTop;
};

function getShowButtons(recordSetsOption: string) {
  let result: string | undefined | null = ['Sector', 'Industry', 'Capitalization'].find((chartType: string) =>
    recordSetsOption.toLowerCase().includes(chartType.toLowerCase())
  );
  ['sharpe-by-capitalization', 'sharpe-by-industry', 'sharpe-by-sector', 'sharpe-by-geography'].find((value: string) => {
    if (value === recordSetsOption) {
      result = null;

      return true;
    }

    return false;
  });

  return result;
}

const dataExplorerConfig = {
  chart: { className: 'data-explorer-graph' },
  legend: {
    useHTML: true,
    labelFormatter: function (this: { name: string; color: string }) {
      // @ts-ignore
      const buttonLightColor = new Highcharts.Color(this.color).brighten(0.1).get('rgba');
      return `<button title="Click to toggle ${this.name} series" class="ui button button--secondary highcharts-legend-item-button">
            <span class="highcharts-legend-item-button__icon" style="background-color:${buttonLightColor}; box-shadow:  0px 0px 6px 3px ${buttonLightColor};"></span>
            <span class="highcharts-legend-item-button__text">${this.name}</span></button>`;
    },
  },
};

export class Chart extends React.Component<ChartProps, ChartState> {
  private chartRef: any;

  constructor(props: ChartProps) {
    super(props);
    this.state = { windowIsResizing: 0, extremes: {} };
  }

  componentDidMount() {
    this.checkIfComponentRendered();
  }

  UNSAFE_componentWillUpdate(nextProps: any) {
    if (this.props.shouldResize !== nextProps.shouldResize) {
      this.checkIfComponentRendered();
    }
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.isFetchingRecordSetChart !== prevProps.isFetchingRecordSetChart) {
      this.checkIfComponentRendered();
    }
    if (this.props.shouldResize !== prevProps.shouldResize) {
      this.checkIfComponentRendered();
    }
    if (
      this.props.recordSetsOption !== prevProps.recordSetsOption ||
      (this.props.alpha && prevProps.alpha && this.props.alpha.get('id') !== prevProps.alpha.get('id'))
    ) {
      this.props.handleSetExtremes({ userMin: null, userMax: null });
    }
  }

  UNSAFE_componentWillMount() {
    window.addEventListener('resize', this.resize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  UNSAFE_componentWillReceiveProps(nextProps: ChartProps) {
    if (this.props.recordSetsOption !== nextProps.recordSetsOption) {
      this.props.handleHideButton?.(false);
      this.props.handleAllButton?.(true);
    }
  }

  shouldComponentUpdate(nextProps: ChartProps, nextState: ChartState) {
    return (
      this.props.recordSetsOption !== nextProps.recordSetsOption ||
      this.props.isFetchingRecordSetChart !== nextProps.isFetchingRecordSetChart ||
      this.props.showCode !== nextProps.showCode ||
      this.props.showLearn !== nextProps.showLearn ||
      (this.props.chartData && nextProps.chartData ? !this.props.chartData.equals(nextProps.chartData) : true) ||
      (!nextProps.shouldResize && this.props.shouldResize !== nextProps.shouldResize) ||
      this.state.windowIsResizing !== nextState.windowIsResizing
    );
  }

  resize = () => {
    this.setState(
      state => ({ windowIsResizing: state.windowIsResizing + 1 }),
      () => this.checkIfComponentRendered()
    );
  };

  handlerHideAll = () => {
    this.props.handleHideButton?.(true);
    this.props.handleAllButton?.(false);
    this.chartRef.chart.legend.allItems.map((serie: any) => serie.setVisible(false, false));
    this.chartRef.chart.redraw();
  };

  changeSvgElementsOrder = () => {
    const navigatorOutline: any = document.querySelector(`#${this.props.id} .highcharts-navigator-outline`);
    const scrollBar: any = document.querySelector(`#${this.props.id} .highcharts-navigator-mask-inside`);
    const handleLeft: any = document.querySelector(`#${this.props.id} .highcharts-navigator-handle-left`);
    const handleRight: any = document.querySelector(`#${this.props.id} .highcharts-navigator-handle-right`);
    const barUnderScrollBar = document.querySelectorAll(`#${this.props.id} .highcharts-navigator-mask-outside`);
    if (navigatorOutline && scrollBar) {
      navigatorOutline.parentNode.insertBefore(navigatorOutline, scrollBar);
      navigatorOutline.parentNode.insertBefore(barUnderScrollBar[1], scrollBar);
      navigatorOutline.parentNode.insertBefore(barUnderScrollBar[0], scrollBar);
      navigatorOutline.parentNode.insertBefore(handleLeft, scrollBar);
      navigatorOutline.parentNode.insertBefore(handleRight, scrollBar);
      setTimeout(() => scrollBar.setAttribute('height', '12'), 100);
    }
  };

  makeNavigatorRound = () => {
    const elementScroll = document.querySelector(`#${this.props.id} .highcharts-navigator-mask-inside`);

    // Query to obtain both masks under the navigator
    // There is one mask to the left and another to the right
    const barUnderScrollBar = document.querySelectorAll(`#${this.props.id} .highcharts-navigator-mask-outside`);
    if (barUnderScrollBar[0]) {
      barUnderScrollBar[0].setAttribute('rx', '4');
    }
    if (barUnderScrollBar[1]) {
      barUnderScrollBar[1].setAttribute('rx', '4');
    }

    this.changeSvgElementsOrder();
    if (elementScroll) {
      elementScroll.setAttribute('rx', '8');
      elementScroll.setAttribute('height', '12');
    }

    if (this.chartRef && this.chartRef.xAxis) {
      const userMin = this.props.extremes.userMin ? this.props.extremes.userMin : null;
      const userMax = this.props.extremes.userMax ? this.props.extremes.userMax : null;
      this.chartRef.xAxis[0].setExtremes(userMin, userMax);
    }
  };

  checkIfComponentRendered = () => {
    const classNameToTest = this.props.fatherClass ? this.props.fatherClass : '.editor-simulation';
    const panel: any = document.querySelector(classNameToTest);
    if (panel) {
      this.setState(
        state => ({ windowIsResizing: state.windowIsResizing + 1 }),
        () => this.makeNavigatorRound()
      );
    } else {
      this.makeNavigatorRound();
    }
  };

  handleHideButton = (numOfSeriesVisible: number) => {
    if (numOfSeriesVisible === 0) {
      this.props.handleHideButton?.(true);
    } else {
      this.props.handleHideButton?.(false);
    }
  };

  handleActiveButton = (numOfSeriesVisible: number) => {
    if (numOfSeriesVisible === this.chartRef.legend.allItems.length) {
      this.props.handleAllButton?.(true);
    } else {
      this.props.handleAllButton?.(false);
    }
  };
  handlerShowAll = () => {
    this.props.handleHideButton?.(false);
    this.props.handleAllButton?.(true);
    this.chartRef.legend.allItems.map((serie: any) => serie.setVisible(true, false));
    this.chartRef.redraw();
  };

  updateSelectedSeries = (e: any) => {
    const visibilitySeries = this.chartRef.legend.allItems
      .map((serie: any, index: number) => {
        if (e.target.index === index) {
          return !serie.visible;
        } else {
          return serie.visible;
        }
      })
      .filter((el: any) => el);
    this.handleHideButton?.(visibilitySeries.length);
    this.handleActiveButton?.(visibilitySeries.length);
  };

  handleChartRef = (ref: any) => {
    this.chartRef = ref;

    if (this.props.getChartRef) {
      this.props.getChartRef(ref);
    }
  };

  handlerCheckHowManySeriesSelected = (chartRef: any) => {
    if (chartRef) {
      const arraySelected = chartRef.chart.legend.allItems.filter((serie: any) => serie.visible);

      return arraySelected.length;
    } else {
      return 0;
    }
  };

  getSeriesNumber = () => {
    if (this.chartRef) {
      return this.chartRef.legend.allItems.length;
    } else {
      return 0;
    }
  };

  handleZoomOut = () => {
    this.chartRef.xAxis[0].setExtremes(null, null);
    const elementScroll = document.querySelector('.highcharts-navigator-mask-inside');
    if (elementScroll) {
      setTimeout(() => elementScroll.setAttribute('height', '12'), 100);
    }
  };

  render() {
    const {
      modifiers = [],
      recordSets,
      recordSetsOption,
      handleChangeRecordSets,
      chartData,
      isFetchingRecordSetChart = false,
      id = 'chart_container',
      alpha,
      currency = null,
      valueType = 'decimal',
      showAllAndNoneButtons = true,
      noResults,
      addId = true,
      isTestPeriodControlShown,
      onChangeTestPeriodShown,
    } = this.props;
    let showButtons = null;

    if (recordSetsOption) {
      showButtons = getShowButtons(recordSetsOption);
    }

    const getNoResultsMessage = () => {
      if (noResults) {
        return noResults;
      }
      if (alpha && alpha.get('author')) {
        return 'chart.noResults.alphas';
      } else {
        return 'chart.noResults';
      }
    };

    const showZoomOutButtonArray: any = [
      'pnl',
      'sharpe',
      'turnover',
      'coverage',
      'dataset-coverage',
      'average-value-by-industry',
      'actual-weight',
      'average-value-by-country',
      'average-value-by-sector',
      'average-value-by-geography',
      'pnl-by-capitalization',
      'pnl-by-country',
      'pnl-by-industry',
      'pnl-by-sector',
      'pnl-by-geography',
      'performance-area',
      'performance-columns',
      'performance-stacked-columns',
    ];
    const heightLoader = getHeightChart({ elementClass: this.props.fatherClass, customNavigatorSize: this.props.customNavigatorSize });
    const newModifiers = showZoomOutButtonArray.includes(recordSetsOption) && !showButtons ? [...modifiers, 'has-navigator'] : modifiers;

    return (
      <>
        {isTestPeriodControlShown && onChangeTestPeriodShown && (
          <TestPeriodControl onChangeTestPeriodShown={onChangeTestPeriodShown} path={this.props.path} eventLabel="notification_button" />
        )}
        <div className={addModifiers('intro-step-7 alphas-details-content__box', newModifiers)} id={addId ? id : undefined}>
          {recordSets || recordSetsOption === 'pnl' ? (
            <div className="chart__header">
              <div className="chart__title">
                {' '}
                <FormattedMessageHelper tagName="span" id="chart.1" defaultMessage={localeData.en['chart.1']} />
              </div>
              <Dropdown
                options={recordSets ? recordSets : performanceArray}
                className="dropdown-custom chart__dropdown"
                fluid={true}
                selection={true}
                disabled={isFetchingRecordSetChart}
                value={recordSetsOption ? recordSetsOption : 'PnL'}
                onChange={handleChangeRecordSets ? handleChangeRecordSets : handleChangeRecordSetsTest}
              />
            </div>
          ) : null}
          {isFetchingRecordSetChart ? (
            <div style={{ height: `${showButtons ? heightLoader + 24 : heightLoader}px` }} className="loader">
              <Loader active={true} inline="centered" size="massive" />
            </div>
          ) : recordSetsOption && chartData && chartData.get('records').size > 0 ? (
            <div style={{ position: 'relative' }}>
              <GetChart
                handleChartRef={this.handleChartRef}
                valueType={valueType}
                chartDataSchema={chartData.get('schema').toJS()}
                chartDataRecords={chartData.get('records').toJS()}
                recordSetsOption={recordSetsOption}
                alphaInfo={alpha}
                updateSelectedSeries={this.updateSelectedSeries}
                fatherClass={this.props.fatherClass}
                customNavigatorSize={this.props.customNavigatorSize}
                handleSetExtremes={this.props.handleSetExtremes}
                currency={currency}
                position={this.props.position}
              />
              {showButtons && showAllAndNoneButtons && (
                <ChartButtons
                  handlerShowAll={this.handlerShowAll}
                  handlerHideAll={this.handlerHideAll}
                  path={this.props.pathNoneAllButtons ? this.props.pathNoneAllButtons : this.props.path}
                />
              )}
              {showZoomOutButtonArray.includes(recordSetsOption) && <div onClick={this.handleZoomOut} className="chart__zoom-out" />}
            </div>
          ) : (
            <div className="chart__no-results">
              <FormattedMessageHelper id={getNoResultsMessage()} />
            </div>
          )}
        </div>
      </>
    );
  }
}
export function getBarChartTooltipFormatter(chartName: string) {
  return function (this: Highcharts.TooltipFormatterContextObject) {
    let tooltipHtml: any = '';
    const isCoverageChart = chartName === 'coverage' || chartName === 'dataset-coverage';
    tooltipHtml += '<table style="margin-top: 16px;">';
    if (this.points?.[0]?.point?.category && isCoverageChart) {
      tooltipHtml += `<tr><td style="font-weight:bold;" colspan="2">${this.points[0].point.category}</td></tr>`;
    }
    this.points?.forEach((entry: any) => {
      const formattedNumber = entry.y.toLocaleString(getLanguageCode(), twoDecimalCaseOption);

      const formattedValue = isCoverageChart ? `${formattedNumber}%` : formattedNumber;

      tooltipHtml += `<tr><td style="font-weight:bold; color:${entry.series.color}">
            ${isCoverageChart ? entry.series.name : entry.x}:
            </td><td style="text-align: right"> ${formattedValue} </td></tr>`;
    });
    tooltipHtml += '</table>';

    return tooltipHtml;
  };
}
